import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../utils/cn';

type ToastVariant = 'success' | 'error';

export type ToastPosition =
  | 'center'
  | 'top-center'
  | 'bottom-center'
  | 'bottom-right'
  | 'bottom-left';

export interface ShowToastOptions {
  variant?: ToastVariant;
  position?: ToastPosition;
}

interface Toast {
  id: number;
  message: string;
  variant: ToastVariant;
  position: ToastPosition;
}

interface ToastContextValue {
  showToast: (
    message: string,
    options?: ToastVariant | ShowToastOptions
  ) => void;
}

interface ToastProviderProps {
  children: React.ReactNode;
  position?: ToastPosition;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_DURATION_MS = 5000;

const TOAST_POSITION_CLASSES: Record<ToastPosition, string> = {
  center:
    'fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none',
  'top-center':
    'fixed top-6 left-4 right-4 z-[10000] flex justify-center pointer-events-none',
  'bottom-center':
    'fixed bottom-6 left-4 right-4 z-[10000] flex justify-center pointer-events-none',
  'bottom-right':
    'fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-[10000]',
  'bottom-left':
    'fixed bottom-6 left-4 right-4 sm:right-auto sm:left-6 sm:max-w-md z-[10000]',
};

function resolveShowToastOptions(
  optionsOrVariant?: ToastVariant | ShowToastOptions
): ShowToastOptions {
  if (typeof optionsOrVariant === 'string') {
    return { variant: optionsOrVariant };
  }
  return optionsOrVariant ?? {};
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position: defaultPosition = 'center',
}) => {
  const [toast, setToast] = useState<Toast | null>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const dismissToast = useCallback(() => {
    clearDismissTimer();
    setToast(null);
  }, [clearDismissTimer]);

  const showToast = useCallback(
    (message: string, optionsOrVariant?: ToastVariant | ShowToastOptions) => {
      const { variant = 'success', position = defaultPosition } =
        resolveShowToastOptions(optionsOrVariant);

      clearDismissTimer();
      setToast({ id: Date.now(), message, variant, position });
      dismissTimerRef.current = setTimeout(dismissToast, TOAST_DURATION_MS);
    },
    [clearDismissTimer, defaultPosition, dismissToast]
  );

  useEffect(() => () => clearDismissTimer(), [clearDismissTimer]);

  const toastPosition = toast?.position ?? defaultPosition;
  const isCenteredLayout =
    toastPosition !== 'bottom-right' && toastPosition !== 'bottom-left';

  const toastPortal =
    toast && typeof document !== 'undefined'
      ? createPortal(
          <div
            className={TOAST_POSITION_CLASSES[toastPosition]}
            role='status'
            aria-live='polite'
          >
            <div
              className={cn(
                'flex items-start gap-3 rounded-xl px-4 py-3 shadow-lg border pointer-events-auto',
                isCenteredLayout && 'w-full max-w-md',
                toast.variant === 'success'
                  ? 'bg-white border-[#006543]/20 text-gray-900'
                  : 'bg-white border-red-200 text-gray-900'
              )}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-sm font-bold',
                  toast.variant === 'success'
                    ? 'bg-[#006543] text-white'
                    : 'bg-red-500 text-white'
                )}
                aria-hidden='true'
              >
                {toast.variant === 'success' ? '✓' : '!'}
              </span>
              <p className='flex-1 text-sm font-inter-semibold pt-0.5'>
                {toast.message}
              </p>
              <button
                type='button'
                onClick={dismissToast}
                className='shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none'
                aria-label='Dismiss notification'
              >
                ×
              </button>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toastPortal}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
